import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { getAllProductPaths, getSingleProduct } from '@lib/shopify'
import { Layout } from '@components/common'
import ProductGallery from '@components/products/ProductGallery'
import { Container } from '@components/ui'
import ProductInfo from '@components/products/ProductInfo'
import { Breadcrumb, PathWithCategory, ShopifyProductMetafield } from '@types'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import cn from 'classnames'
import Head from 'next/head'

export async function getStaticProps({
  params,
  locale,
}: GetStaticPropsContext<{ handle: string }>) {
  const product = await getSingleProduct(params!.handle, locale || 'en-US')

  if (!product) {
    throw new Error(`Product with slug '${params!.handle}' not found`)
  }

  return {
    props: {
      ...(await serverSideTranslations(locale || 'en-US', ['common', 'cart'])),
      product,
    },
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  let paths: string[] = []

  if (locales) {
    for (const locale of locales) {
      const res: PathWithCategory[] = await getAllProductPaths(locale)

      if (res.length > 0) {
        paths = paths.concat(
          res.map(
            (p: PathWithCategory): string => `/${locale}/products/${p.slug}`
          )
        )
      }
    }
  }

  return {
    paths,
    fallback: false,
  }
}

export default function Handle({
  product,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { t } = useTranslation(['common'])

  const category =
    product.collections.edges[0].node.handle === 'frontpage'
      ? product.collections.edges[1].node.handle
      : product.collections.edges[0].node.handle

  const breadcrumbs: Breadcrumb[] = [
    { path: '/', label: t('common:breadcrumbs.home') },
    { path: '/products', label: t('common:breadcrumbs.products') },
    {
      path: `/products/${category}`,
      label: t(`common:categories.${category}`),
    },
    { label: product.title },
  ]

  const sizeChart: ShopifyProductMetafield | undefined = product.metafield

  return (
    <>
      <Head>
        <title>
          {t('common:metaTitles.product', {
            name: product.title,
            category: t(`common:categories.${category}`),
          })}
        </title>
        <meta name="description" content={product.description} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={product.title} />
        <meta property="og:image" content={product.images.edges[0].node.url} />
        <meta
          property="og:image:alt"
          content={product.images.edges[0].node.altText || product.title}
        />
        <meta property="og:description" content={product.description} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={product.title} />
        <meta name="twitter:description" content={product.description} />
        <meta name="twitter:image" content={product.images.edges[0].node.url} />
        <meta
          name="twitter:image:alt"
          content={product.images.edges[0].node.altText || product.title}
        />
      </Head>
      <Container className="py-20 md:py-32">
        <ul className="flex flex-wrap gap-4 items-center">
          {breadcrumbs.map((b, i) => (
            <>
              {i > 0 && <small>/</small>}
              <li key={b.label} className={cn({ 'color-wine': !!b.path })}>
                {!!b.path ? (
                  <Link href={b.path}>{b.label}</Link>
                ) : (
                  <span>{b.label}</span>
                )}
              </li>
            </>
          ))}
        </ul>
        <div className="grid grid-cols-1 gap-8 mt-10 lg:grid-cols-2 lg:gap-8 lg:mt-20">
          <ProductGallery
            images={product.images.edges}
            productTitle={product.title}
          />
          <ProductInfo
            title={product.title}
            description={product.descriptionHtml}
            price={parseInt(product.priceRange.minVariantPrice.amount)}
            currencyCode={product.priceRange.minVariantPrice.currencyCode.toLowerCase()}
            variants={product.variants.edges.map((e: any) => e.node)}
            sizes={product.options[0].values}
            sizeChart={sizeChart}
            handle={product.handle}
          />
        </div>
      </Container>
    </>
  )
}

Handle.Layout = Layout
